/* =====================================================================
 * GlobalStyle — reset, body/link/scrollbar/selection, and shared
 * keyframes. Consumes AppTheme via styled-components ThemeProvider.
 *
 * Migrated verbatim from askyourai/src/theme/GlobalStyle.ts so the B2C
 * render is byte-identical. The one `.temml-error` `#cc0000` literal
 * is kept as-is for Phase 1 — it'll be moved to `palette.feedback.math`
 * (or similar) during Phase 2's color sweep.
 * ===================================================================== */

import { createGlobalStyle } from 'styled-components';
import type { AppTheme } from './types';

/**
 * Augment styled-components' DefaultTheme so `${({ theme }) => ...}`
 * callbacks inside this package type-check. Consumers (e.g. the B2C
 * askyourai app) can add their own identical augmentation — duplicate
 * interface declarations with matching shapes are merged by TS.
 */
declare module 'styled-components' {
  export interface DefaultTheme extends AppTheme {}
}

export const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16.5px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  body {
    font-family: ${({ theme }) => theme.fonts.sans};
    background: ${({ theme }) => theme.colors.bgPrimary};
    color: ${({ theme }) => theme.colors.textPrimary};
    line-height: 1.6;
    overflow: hidden;
    height: 100vh;
    width: 100vw;
  }

  #root {
    height: 100%;
    width: 100%;
  }

  a {
    color: ${({ theme }) => theme.colors.textLink};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  code {
    font-family: ${({ theme }) => theme.fonts.mono};
  }

  button {
    cursor: pointer;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
  }

  input, textarea {
    font-family: inherit;
    outline: none;
    border: none;
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.scrollbar};
    border-radius: 3px;
    &:hover {
      background: ${({ theme }) => theme.colors.scrollbarHover};
    }
  }

  /* Mobile: slim scrollbar */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ::-webkit-scrollbar {
      width: 3px;
      height: 3px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 2px;
    }
  }

  /* Selection */
  ::selection {
    background: ${({ theme }) => theme.colors.accentLight};
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  /* Temml (MathML) overrides for streaming stability */
  math[display="block"] {
    display: block;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.5em 0;
    text-align: center;
  }

  .temml-error {
    color: #cc0000;
    font-size: 0.875rem;
  }

  /* Markdown animation */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  @keyframes slideInRight {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes slideOutRight {
    from { transform: translateX(0); opacity: 1; }
    to { transform: translateX(100%); opacity: 0; }
  }

  @keyframes slideInLeft {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }
`;
